import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdminDashboardDashboardStats } from "../../store/adminSlice";
import "./AdminDashboard.css";
import { Link } from "react-router-dom";

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { dashboardStats, loading, error } = useSelector(
    (state) => state.admin,
  );

  useEffect(() => {
    dispatch(fetchAdminDashboardDashboardStats());
  }, [dispatch]);

  if (loading)
    return (
      <div className="admin-loader-text">
        Compiling Global Enterprise Architecture Metrics Ledger...
      </div>
    );
  if (error) return <div className="admin-error-fallback-panel"> {error}</div>;
  if (!dashboardStats) return null;

  // Destructure metrics with safety fallbacks to handle empty database states gracefully
  const kpis = dashboardStats.kpis || {
    total_revenue: 0,
    total_orders: 0,
    total_customers: 0,
    low_stock_alerts: 0,
  };
  const revenue_trends = dashboardStats.revenue_trends || [];
  const top_products = dashboardStats.top_products || [];
  // eslint-disable-next-line no-unused-vars
  const order_statuses = dashboardStats.order_statuses || [];

  // Helper computation math map layer to safely scale CSS graph elements
  const maxRevenueValue =
    revenue_trends.length > 0
      ? Math.max(
          ...revenue_trends.map((t) => parseFloat(t.monthly_revenue || 0)),
        )
      : 1;

  return (
    <div className="dashboard-root-layout">
      <header className="dashboard-title-bar">
        <div>
          <h1>Executive Analytics Command Panel</h1>
          <p className="subtitle-date-stamp">
            System Ledger Status Pipeline Realtime: Live Production Mode
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={() => window.print()} className="btn-print">
            <i class="material-icons">&#xe8ad;</i> Print
          </button>
        </div>
      </header>

      {/* KPI Counter Deck Row */}
      <section className="kpi-counters-deck-grid">
        <div className="kpi-card-metric">
          <span className="kpi-lbl">Accumulated Revenue Ledger</span>
          <h2 className="kpi-val text-green">
            {/* Force a fallback to 0 if the backend value returns null */}₹
            {parseFloat(kpis.total_revenue || 0).toLocaleString("en-IN")}
          </h2>
        </div>
        <div className="kpi-card-metric">
          <span className="kpi-lbl">Fulfillment Orders Volume</span>
          <h2 className="kpi-val">
            {parseInt(kpis.total_orders || 0).toLocaleString()}
          </h2>
        </div>
        <div className="kpi-card-metric">
          <span className="kpi-lbl">Registered Customers Base</span>
          <h2 className="kpi-val">{kpis.total_customers || 0}</h2>
        </div>
        <div className="kpi-card-metric critical-alert-border">
          <span className="kpi-lbl">Low Inventory Warnings</span>
          <h2 className="kpi-val text-red">{kpis.low_stock_alerts || 0} SKU</h2>
        </div>
      </section>

      {/* Data Analytical Grid Structure splits */}
      <div className="dashboard-analytics-split-grid">
        {/* Visual Native CSS Bar chart matrix element wrapper */}
        <div className="analytics-block-frame">
          <h3>6-Month Rolling Financial Revenue Runway Trends</h3>
          <div className="native-bar-chart-container">
            {revenue_trends.length === 0 ? (
              <p style={{ color: "#777", padding: "20px" }}>
                No financial trends compiled for this date window context.
              </p>
            ) : (
              revenue_trends.map((trend, i) => {
                const currentRevenue = parseFloat(trend.monthly_revenue || 0);
                const barHeightPercent =
                  (currentRevenue / maxRevenueValue) * 100;
                return (
                  <div key={i} className="chart-bar-column">
                    <div className="bar-data-tooltip">
                      ₹{parseInt(currentRevenue).toLocaleString("en-IN")}
                    </div>
                    <div
                      className="actual-bar-fill-element"
                      style={{ height: `${Math.max(barHeightPercent, 8)}%` }}
                    ></div>
                    <span className="chart-axis-lbl">{trend.month_label}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Top Performing Inventory Leaderboards Array table */}
        <div className="analytics-block-frame">
          <h3>Top 5 High-Velocity Product SKUs</h3>
          <div className="leaderboard-list-table-frame">
            {top_products.length === 0 ? (
              <p style={{ color: "#777", padding: "20px" }}>
                No sales metrics registered yet.
              </p>
            ) : (
              top_products.map((item, idx) => (
                <div key={idx} className="leaderboard-item-row-strip">
                  <div className="leaderboard-item-details-meta">
                    <span className="rank-indicator">0{idx + 1}</span>
                    <p className="product-rank-name-lbl">
                      <strong>{item.product_name}</strong>
                    </p>
                  </div>
                  <div className="leaderboard-numeric-aggregates-block">
                    <span className="volume-sold-count-badge">
                      {item.volume_sold || 0} Units Sold
                    </span>
                    <strong className="gross-revenue-metric-text">
                      ₹
                      {parseFloat(item.gross_generated || 0).toLocaleString(
                        "en-IN",
                      )}
                    </strong>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
